import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import { Col, PageHeader, Row, Spin, Tabs, Tag, message, Drawer } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import {
    ButtonItem,
    RangePickerItem,
    TableItem,
    SelectItem,
    InputNumberItem,
} from "../../../Components/Items";
import KpiCard from "./KpiCard";
import Recursos from "./Recursos";
import Alertas from "./Alertas";
import { formatMs, colorMs, tagMetodo, truncarPath } from "./utilsPerformance";

moment.locale("es");

const { TabPane } = Tabs;

// Cuántos renglones se traen al abrir el detalle de un endpoint. TableItem no reenvía el
// prop `pagination` (fija el suyo), así que la paginación del servidor nunca se podía
// disparar; se trae un bloque y se pagina del lado del cliente.
const TAM_DETALLE = 500;

class PerformanceRequests extends Component {
    state = {
        loadingResumen: false,
        loadingTop: false,
        loadingListar: false,

        resumen: {
            total: 0,
            avgMs: 0,
            maxMs: 0,
            mayorA5s: 0,
            mayorA10s: 0,
        },
        saturacionPorMinuto: [],

        topEndpoints: [],
        agruparNormalizado: true,
        limitTop: 20,
        metricaBarras: "avgMs",

        drawerVisible: false,
        drawerPath: null,
        drawerMethod: null,
        drawerData: [],
        drawerTotal: 0,
        drawerMinDurationMs: null,
    };

    componentDidMount() {
        if (this.props.activo) this.consultarTodo();
    }

    componentDidUpdate(prevProps) {
        const cambioRango = prevProps.rango !== this.props.rango;
        const pidieronRefrescar = prevProps.refrescarNonce !== this.props.refrescarNonce;
        const seActivo = !prevProps.activo && this.props.activo;

        // Sólo se consulta con la pestaña a la vista: cambiar el rango desde Recursos no
        // debe disparar peticiones de esta pestaña que nadie está viendo.
        if (this.props.activo && (cambioRango || pidieronRefrescar)) {
            this.consultarTodo();
            return;
        }
        if (seActivo && (!this.state.cargado || cambioRango || pidieronRefrescar)) {
            this.consultarTodo();
        }
    }

    rangoParams() {
        const [desde, hasta] = this.props.rango || [];
        return {
            desde: desde ? desde.toISOString() : undefined,
            hasta: hasta ? hasta.toISOString() : undefined,
        };
    }

    consultarTodo = () => {
        this.setState({ cargado: true });
        this.consultarResumen();
        this.consultarTopEndpoints();
    };

    consultarResumen = () => {
        this.setState({ loadingResumen: true });
        axios
            .post(
                "performance/resumen",
                { ...this.rangoParams() },
                { headers: this.props.headersToken }
            )
            .then((res) => {
                this.setState({ loadingResumen: false });
                // success 2 = OK. Este router respondía 1, que es el mismo valor con el
                // que validarUsuarioBiwo rechaza; ahora se distinguen.
                if (res.data.success === 2) {
                    this.setState({
                        resumen: res.data.resumen || {
                            total: 0,
                            avgMs: 0,
                            maxMs: 0,
                            mayorA5s: 0,
                            mayorA10s: 0,
                        },
                        saturacionPorMinuto: res.data.saturacionPorMinuto || [],
                    });
                } else {
                    message.error("Error al consultar el resumen de performance");
                }
            })
            .catch(() => {
                this.setState({ loadingResumen: false });
                message.error("Error al consultar el resumen de performance");
            });
    };

    consultarTopEndpoints = () => {
        this.setState({ loadingTop: true });
        axios
            .post(
                "performance/topEndpoints",
                {
                    ...this.rangoParams(),
                    limit: this.state.limitTop,
                    agruparNormalizado: this.state.agruparNormalizado,
                },
                { headers: this.props.headersToken }
            )
            .then((res) => {
                this.setState({ loadingTop: false });
                if (res.data.success === 2) {
                    this.setState({ topEndpoints: res.data.data || [] });
                } else {
                    message.error("Error al consultar los endpoints más lentos");
                }
            })
            .catch(() => {
                this.setState({ loadingTop: false });
                message.error("Error al consultar los endpoints más lentos");
            });
    };

    consultarListar = () => {
        this.setState({ loadingListar: true });
        axios
            .post(
                "performance/listar",
                {
                    ...this.rangoParams(),
                    page: 1,
                    pageSize: TAM_DETALLE,
                    sortField: "durationMs",
                    sortOrder: "descend",
                    method: this.state.drawerMethod || undefined,
                    path: this.state.drawerPath || undefined,
                    minDurationMs: this.state.drawerMinDurationMs || undefined,
                },
                { headers: this.props.headersToken }
            )
            .then((res) => {
                this.setState({ loadingListar: false });
                if (res.data.success === 2) {
                    this.setState({
                        drawerData: res.data.data || [],
                        drawerTotal: res.data.total || 0,
                    });
                } else {
                    message.error("Error al consultar el detalle");
                }
            })
            .catch(() => {
                this.setState({ loadingListar: false });
                message.error("Error al consultar el detalle");
            });
    };

    abrirDrawer = (record) => {
        this.setState(
            {
                drawerVisible: true,
                drawerPath: record?.path || null,
                drawerMethod: record?.method || null,
                drawerData: [],
                drawerTotal: 0,
                drawerMinDurationMs: null,
            },
            this.consultarListar
        );
    };

    cerrarDrawer = () => {
        this.setState({
            drawerVisible: false,
            drawerPath: null,
            drawerMethod: null,
            drawerData: [],
            drawerTotal: 0,
        });
    };

    // Delegan a utilsPerformance para que las dos pestañas usen el mismo formato y los
    // mismos umbrales de color: "3.2 s" y el rojo de >10 s significan lo mismo en toda
    // la pantalla.
    formatMs = (ms) => formatMs(ms);

    colorMs = (ms) => colorMs(ms);

    tagMetodo = (method) => tagMetodo(method);

    construirDataGrafico = () => {
        const data = (this.state.saturacionPorMinuto || []).map((p) => ({
            x: p._id,
            y: p.count,
        }));
        return [
            {
                id: "Requests >1s por minuto",
                data,
            },
        ];
    };

    truncarPath = (path) => truncarPath(path);

    construirDataBarras = () => {
        const metrica = this.state.metricaBarras;
        return (this.state.topEndpoints || [])
            .slice()
            .sort((a, b) => (b[metrica] || 0) - (a[metrica] || 0))
            .slice(0, 10)
            .map((d) => ({
                endpoint: `${d.method} ${this.truncarPath(d.path)}`,
                pathCompleto: `${d.method} ${d.path}`,
                valor: Math.round(d[metrica] || 0),
            }))
            .reverse();
    };

    construirDataPie = () => {
        const { total = 0, mayorA5s = 0, mayorA10s = 0 } = this.state.resumen || {};
        const entre1y5 = Math.max(0, total - mayorA5s);
        const entre5y10 = Math.max(0, mayorA5s - mayorA10s);
        const masDe10 = Math.max(0, mayorA10s);
        return [
            { id: "1 - 5 s", label: "1 - 5 s", value: entre1y5, color: "#52c41a" },
            { id: "5 - 10 s", label: "5 - 10 s", value: entre5y10, color: "#fa8c16" },
            { id: "> 10 s", label: "> 10 s", value: masDe10, color: "#cf1322" },
        ].filter((d) => d.value > 0);
    };

    render() {
        const { resumen } = this.state;

        const columnsTop = [
            {
                title: "Método",
                dataIndex: "method",
                key: "method",
                width: 90,
                render: (v) => this.tagMetodo(v),
            },
            {
                title: "Endpoint",
                dataIndex: "path",
                key: "path",
                render: (v) => (
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>{v}</span>
                ),
            },
            {
                title: "Veces",
                dataIndex: "count",
                key: "count",
                width: 80,
                align: "right",
                sorter: (a, b) => a.count - b.count,
            },
            {
                title: "Prom.",
                dataIndex: "avgMs",
                key: "avgMs",
                width: 110,
                align: "right",
                sorter: (a, b) => a.avgMs - b.avgMs,
                defaultSortOrder: "descend",
                render: (v) => (
                    <span style={{ color: this.colorMs(v), fontWeight: "bold" }}>
                        {this.formatMs(v)}
                    </span>
                ),
            },
            {
                title: "p95",
                dataIndex: "p95Ms",
                key: "p95Ms",
                width: 110,
                align: "right",
                sorter: (a, b) => (a.p95Ms || 0) - (b.p95Ms || 0),
                render: (v) => (
                    <span style={{ color: this.colorMs(v) }}>{this.formatMs(v)}</span>
                ),
            },
            {
                title: "Máx.",
                dataIndex: "maxMs",
                key: "maxMs",
                width: 110,
                align: "right",
                sorter: (a, b) => a.maxMs - b.maxMs,
                render: (v) => (
                    <span style={{ color: this.colorMs(v) }}>{this.formatMs(v)}</span>
                ),
            },
            {
                title: "Mín.",
                dataIndex: "minMs",
                key: "minMs",
                width: 100,
                align: "right",
                render: (v) => this.formatMs(v),
            },
            {
                title: "Total",
                dataIndex: "totalMs",
                key: "totalMs",
                width: 120,
                align: "right",
                sorter: (a, b) => a.totalMs - b.totalMs,
                render: (v) => (
                    <span style={{ fontWeight: "bold" }}>{this.formatMs(v)}</span>
                ),
            },
        ];

        const columnsListar = [
            {
                title: "Fecha",
                dataIndex: "createdAt",
                key: "createdAt",
                width: 160,
                render: (v) => (v ? moment(v).format("DD/MM/YYYY HH:mm:ss") : "-"),
            },
            {
                title: "Método",
                dataIndex: "method",
                key: "method",
                width: 80,
                render: (v) => this.tagMetodo(v),
            },
            {
                title: "URL",
                dataIndex: "originalUrl",
                key: "originalUrl",
                render: (v, record) => (
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                        {v || record.path}
                    </span>
                ),
            },
            {
                title: "Duración",
                dataIndex: "durationMs",
                key: "durationMs",
                width: 110,
                align: "right",
                render: (v) => (
                    <span style={{ color: this.colorMs(v), fontWeight: "bold" }}>
                        {this.formatMs(v)}
                    </span>
                ),
            },
            {
                title: "Status",
                dataIndex: "statusCode",
                key: "statusCode",
                width: 80,
                align: "center",
                render: (v) => {
                    if (!v) return "-";
                    let color = "green";
                    if (v >= 500) color = "red";
                    else if (v >= 400) color = "orange";
                    else if (v >= 300) color = "blue";
                    return <Tag color={color}>{v}</Tag>;
                },
            },
            {
                title: "IP",
                dataIndex: "ip",
                key: "ip",
                width: 130,
            },
            {
                title: "Empresa",
                dataIndex: "empresaId",
                key: "empresaId",
                width: 220,
                render: (v) => (
                    <span style={{ fontFamily: "monospace", fontSize: 11 }}>{v || "-"}</span>
                ),
            },
            {
                title: "Usuario",
                dataIndex: "usuarioId",
                key: "usuarioId",
                width: 220,
                render: (v) => (
                    <span style={{ fontFamily: "monospace", fontSize: 11 }}>{v || "-"}</span>
                ),
            },
        ];

        // El PageHeader, el rango y el botón de refrescar viven en el shell de abajo:
        // los comparten las dos pestañas.
        return (
            <>
                <Spin spinning={this.state.loadingResumen}>
                    <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
                        <Col xs={12} sm={12} md={8} lg={Math.floor(24 / 5)} xl={Math.floor(24 / 5)}>
                            <KpiCard
                                titulo="Total requests > 1s"
                                valor={(resumen.total || 0).toLocaleString()}
                                color="#00BFD6"
                            />
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={Math.floor(24 / 5)} xl={Math.floor(24 / 5)}>
                            <KpiCard
                                titulo="Promedio"
                                valor={this.formatMs(resumen.avgMs)}
                                color={this.colorMs(resumen.avgMs || 0)}
                            />
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={Math.floor(24 / 5)} xl={Math.floor(24 / 5)}>
                            <KpiCard
                                titulo="Máximo"
                                valor={this.formatMs(resumen.maxMs)}
                                color={this.colorMs(resumen.maxMs || 0)}
                            />
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={Math.floor(24 / 5)} xl={Math.floor(24 / 5)}>
                            <KpiCard
                                titulo="Mayor a 5s"
                                valor={(resumen.mayorA5s || 0).toLocaleString()}
                                color="#fa8c16"
                            />
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={Math.floor(24 / 5)} xl={Math.floor(24 / 5)}>
                            <KpiCard
                                titulo="Mayor a 10s"
                                valor={(resumen.mayorA10s || 0).toLocaleString()}
                                color="#cf1322"
                            />
                        </Col>
                    </Row>

                    <div
                        style={{
                            backgroundColor: "white",
                            padding: 15,
                            marginBottom: 10,
                            height: 360,
                            borderRadius: 5,
                            boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                        }}
                    >
                        <h3 style={{ margin: 0, marginBottom: 10 }}>
                            Saturación por minuto (requests &gt; 1s)
                        </h3>
                        {this.state.saturacionPorMinuto.length === 0 ? (
                            <div
                                style={{
                                    height: 280,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#999",
                                }}
                            >
                                Sin datos en el rango seleccionado
                            </div>
                        ) : (
                            <div style={{ height: 290 }}>
                                <ResponsiveLine
                                    data={this.construirDataGrafico()}
                                    margin={{ top: 15, right: 30, bottom: 70, left: 60 }}
                                    enableSlices="x"
                                    useMesh={true}
                                    xScale={{ type: "point" }}
                                    yScale={{
                                        type: "linear",
                                        min: 0,
                                        max: "auto",
                                        stacked: false,
                                        reverse: false,
                                    }}
                                    curve="monotoneX"
                                    lineWidth={2}
                                    pointSize={4}
                                    pointBorderWidth={1}
                                    pointColor={{ theme: "background" }}
                                    pointBorderColor={{ from: "serieColor" }}
                                    colors={["#00BFD6"]}
                                    axisTop={null}
                                    axisRight={null}
                                    axisBottom={{
                                        tickSize: 5,
                                        tickPadding: 8,
                                        tickRotation: 45,
                                        tickValues: (this.construirDataGrafico()[0]?.data || [])
                                            .map((d) => d.x)
                                            .filter((_, i, arr) => {
                                                const step = Math.max(1, Math.floor(arr.length / 12));
                                                return i % step === 0;
                                            }),
                                    }}
                                    axisLeft={{
                                        tickSize: 5,
                                        tickPadding: 8,
                                        legend: "requests / min",
                                        legendOffset: -45,
                                        legendPosition: "middle",
                                    }}
                                    enableGridX={false}
                                    enableArea={true}
                                    areaOpacity={0.15}
                                />
                            </div>
                        )}
                    </div>
                </Spin>

                <Spin spinning={this.state.loadingTop || this.state.loadingResumen}>
                    <Row gutter={10} style={{ marginBottom: 10 }}>
                        <Col xs={24} lg={16}>
                            <div
                                style={{
                                    backgroundColor: "white",
                                    padding: 15,
                                    height: 420,
                                    borderRadius: 5,
                                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                                }}
                            >
                                <Row align="middle" justify="space-between" style={{ marginBottom: 10 }}>
                                    <Col>
                                        <h3 style={{ margin: 0 }}>Top 10 endpoints más lentos</h3>
                                    </Col>
                                    <Col>
                                        <SelectItem
                                            style={{ width: 220 }}
                                            value={this.state.metricaBarras}
                                            onChange={(v) => this.setState({ metricaBarras: v })}
                                            options={[
                                                { value: "avgMs", label: "Promedio (avg)" },
                                                { value: "maxMs", label: "Máximo (max)" },
                                                { value: "p95Ms", label: "Percentil 95 (p95)" },
                                                { value: "totalMs", label: "Tiempo total acumulado" },
                                            ]}
                                        />
                                    </Col>
                                </Row>
                                {this.construirDataBarras().length === 0 ? (
                                    <div
                                        style={{
                                            height: 320,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#999",
                                        }}
                                    >
                                        Sin datos en el rango seleccionado
                                    </div>
                                ) : (
                                    <div style={{ height: 340 }}>
                                        <ResponsiveBar
                                            data={this.construirDataBarras()}
                                            keys={["valor"]}
                                            indexBy="endpoint"
                                            layout="horizontal"
                                            margin={{ top: 10, right: 30, bottom: 50, left: 280 }}
                                            padding={0.25}
                                            valueScale={{ type: "linear" }}
                                            indexScale={{ type: "band", round: true }}
                                            colors={({ data }) => this.colorMs(data.valor)}
                                            borderRadius={3}
                                            borderColor={{ from: "color", modifiers: [["darker", 1.3]] }}
                                            axisTop={null}
                                            axisRight={null}
                                            axisBottom={{
                                                tickSize: 5,
                                                tickPadding: 8,
                                                format: (v) => this.formatMs(v),
                                                legend: this.state.metricaBarras === "totalMs"
                                                    ? "Tiempo total acumulado"
                                                    : "Duración",
                                                legendPosition: "middle",
                                                legendOffset: 38,
                                            }}
                                            axisLeft={{ tickSize: 5, tickPadding: 6 }}
                                            enableGridY={false}
                                            label={(d) => this.formatMs(d.value)}
                                            labelSkipWidth={40}
                                            labelTextColor="#fff"
                                            tooltip={({ value, indexValue, color, data }) => (
                                                <div
                                                    style={{
                                                        padding: 10,
                                                        background: "white",
                                                        border: "1px solid rgba(0,0,0,0.08)",
                                                        borderRadius: 8,
                                                        boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                                                        maxWidth: 420,
                                                    }}
                                                >
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                        <span
                                                            style={{
                                                                width: 10,
                                                                height: 10,
                                                                borderRadius: 999,
                                                                background: color,
                                                                display: "inline-block",
                                                            }}
                                                        />
                                                        <strong style={{ fontSize: 12, fontFamily: "monospace" }}>
                                                            {data.pathCompleto || indexValue}
                                                        </strong>
                                                    </div>
                                                    <div style={{ marginTop: 6, fontSize: 13 }}>
                                                        <strong>{this.formatMs(value)}</strong>
                                                    </div>
                                                </div>
                                            )}
                                        />
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col xs={24} lg={8}>
                            <div
                                style={{
                                    backgroundColor: "white",
                                    padding: 15,
                                    height: 420,
                                    borderRadius: 5,
                                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                                }}
                            >
                                <h3 style={{ margin: 0, marginBottom: 10 }}>Distribución por severidad</h3>
                                {this.construirDataPie().length === 0 ? (
                                    <div
                                        style={{
                                            height: 340,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#999",
                                        }}
                                    >
                                        Sin datos
                                    </div>
                                ) : (
                                    <div style={{ height: 340 }}>
                                        <ResponsivePie
                                            data={this.construirDataPie()}
                                            margin={{ top: 10, right: 20, bottom: 60, left: 20 }}
                                            innerRadius={0.55}
                                            padAngle={1}
                                            cornerRadius={3}
                                            colors={({ data }) => data.color}
                                            borderWidth={1}
                                            borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
                                            arcLabel={(d) => `${d.value.toLocaleString()}`}
                                            arcLabelsTextColor="#fff"
                                            arcLinkLabel={(d) => `${d.label}`}
                                            arcLinkLabelsSkipAngle={8}
                                            arcLinkLabelsTextColor="#555"
                                            arcLinkLabelsThickness={1}
                                            arcLinkLabelsColor={{ from: "color" }}
                                            legends={[
                                                {
                                                    anchor: "bottom",
                                                    direction: "row",
                                                    translateY: 40,
                                                    itemWidth: 80,
                                                    itemHeight: 14,
                                                    symbolSize: 10,
                                                    symbolShape: "circle",
                                                },
                                            ]}
                                        />
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Spin>

                <div
                    style={{
                        backgroundColor: "white",
                        padding: 15,
                        borderRadius: 5,
                        boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    }}
                >
                    <Row align="middle" justify="space-between" style={{ marginBottom: 10 }}>
                        <Col>
                            <h3 style={{ margin: 0 }}>Endpoints más lentos</h3>
                        </Col>
                        <Col>
                            <Row gutter={10} align="middle">
                                <Col>
                                    <span style={{ fontSize: 12, color: "#666" }}>Límite:</span>
                                </Col>
                                <Col>
                                    <InputNumberItem
                                        min={5}
                                        max={200}
                                        value={this.state.limitTop}
                                        onChange={(v) =>
                                            this.setState({ limitTop: v || 20 }, this.consultarTopEndpoints)
                                        }
                                        style={{ width: 80 }}
                                    />
                                </Col>
                                <Col>
                                    <SelectItem
                                        style={{ width: 200 }}
                                        value={this.state.agruparNormalizado ? 1 : 0}
                                        onChange={(v) =>
                                            this.setState(
                                                { agruparNormalizado: v === 1 },
                                                this.consultarTopEndpoints
                                            )
                                        }
                                        options={[
                                            { value: 1, label: "Agrupar (/:id, /:n)" },
                                            { value: 0, label: "Path exacto" },
                                        ]}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    {/* TableItem no reenvía `pagination` ni `scroll` (fija los suyos) y su
                        rowKey por default es `value._id`, que aquí no existe porque los
                        renglones vienen de una agregación: había que darle uno propio. */}
                    <TableItem
                        loading={this.state.loadingTop}
                        dataSource={this.state.topEndpoints}
                        columns={columnsTop}
                        size="small"
                        rowKey={(r) => `${r.method} ${r.path}`}
                        defaultPageSize={20}
                        onRow={(record) => ({
                            onClick: () => this.abrirDrawer(record),
                            style: { cursor: "pointer" },
                        })}
                    />
                </div>

                <Drawer
                    title={
                        <span style={{ fontFamily: "monospace", fontSize: 13 }}>
                            {this.state.drawerMethod} {this.state.drawerPath}
                        </span>
                    }
                    placement="right"
                    width={Math.min(window.innerWidth - 50, 1200)}
                    /* antd 4.15.6 sólo entiende `visible`: su Drawer hace {...rest} y
                       después sobreescribe con `open: visible`, así que pasando `open`
                       el drawer no abría NUNCA y todo este detalle era código muerto. */
                    visible={this.state.drawerVisible}
                    onClose={this.cerrarDrawer}
                    destroyOnClose
                >
                    <Row gutter={10} style={{ marginBottom: 10 }} align="middle">
                        <Col>
                            <span style={{ fontSize: 12, color: "#666" }}>Mín. duración (ms):</span>
                        </Col>
                        <Col>
                            <InputNumberItem
                                min={0}
                                step={500}
                                value={this.state.drawerMinDurationMs}
                                onChange={(v) => this.setState({ drawerMinDurationMs: v })}
                                style={{ width: 120 }}
                                placeholder="0"
                            />
                        </Col>
                        <Col>
                            <ButtonItem
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={this.consultarListar}
                            >
                                Aplicar
                            </ButtonItem>
                        </Col>
                        <Col flex="auto" style={{ textAlign: "right" }}>
                            <span style={{ color: "#666" }}>
                                Mostrando {this.state.drawerData.length.toLocaleString()} de{" "}
                                <strong>{this.state.drawerTotal.toLocaleString()}</strong>
                            </span>
                        </Col>
                    </Row>
                    <TableItem
                        loading={this.state.loadingListar}
                        dataSource={this.state.drawerData}
                        columns={columnsListar}
                        size="small"
                        rowKey={(r) => r._id}
                        defaultPageSize={50}
                    />
                </Drawer>
            </>
        );
    }
}

/**
 * Shell de la pantalla: dueño del encabezado, del rango y de la pestaña activa.
 *
 * Las dos pestañas comparten el rango a propósito —contestan la misma pregunta desde
 * dos ángulos— pero sólo consulta la que está a la vista. El `refrescarNonce` es lo que
 * le avisa a la pestaña activa que el usuario pidió refrescar sin cambiar el rango.
 */
class Performance extends Component {
    state = {
        rango: [moment().subtract(24, "hours"), moment()],
        activeTab: "requests",
        refrescarNonce: 0,
    };

    render() {
        const { rango, activeTab, refrescarNonce } = this.state;
        const comunes = {
            headersToken: this.props.headersToken,
            rango,
            refrescarNonce,
        };

        return (
            <>
                <PageHeader
                    title="Performance"
                    subTitle="Análisis interno de tiempos de respuesta y recursos del servidor"
                    style={{
                        border: "1px solid rgb(235, 237, 240)",
                        backgroundColor: "white",
                        marginBottom: 10,
                    }}
                    extra={
                        <>
                            <RangePickerItem
                                showTime={{ format: "HH:mm" }}
                                format="DD/MM/YYYY HH:mm"
                                value={rango}
                                onChange={(r) => this.setState({ rango: r })}
                                allowClear
                                /* El ancho va por el prop propio: RangePickerItem hace
                                   {...this.props} DESPUÉS de style, así que un `style`
                                   del caller le pisa el borde calculado. */
                                width={360}
                                ranges={{
                                    "Última hora": [moment().subtract(1, "hours"), moment()],
                                    "Últimas 6 hrs": [moment().subtract(6, "hours"), moment()],
                                    "Últimas 24 hrs": [moment().subtract(24, "hours"), moment()],
                                    "Últimos 7 días": [moment().subtract(7, "days"), moment()],
                                    "Últimos 30 días": [moment().subtract(30, "days"), moment()],
                                }}
                            />
                            <ButtonItem
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={() =>
                                    this.setState((prev) => ({
                                        refrescarNonce: prev.refrescarNonce + 1,
                                    }))
                                }
                            >
                                Refrescar
                            </ButtonItem>
                        </>
                    }
                />

                {/* Tabs por children y no por `items`: esa API llegó en antd 4.23 y aquí
                    hay 4.15.6. Y SIN forceRender: una gráfica de Nivo montada en un pane
                    oculto mide 0×0 y no dibuja nada (shouldRender exige ancho y alto). */}
                <Tabs
                    activeKey={activeTab}
                    onChange={(k) => this.setState({ activeTab: k })}
                    style={{ backgroundColor: "white", padding: "0 15px", borderRadius: 5 }}
                >
                    <TabPane tab="Requests" key="requests">
                        <PerformanceRequests {...comunes} activo={activeTab === "requests"} />
                    </TabPane>
                    <TabPane tab="Recursos del servidor" key="recursos">
                        <Recursos {...comunes} activo={activeTab === "recursos"} />
                    </TabPane>
                    <TabPane tab="Alertas" key="alertas">
                        <Alertas {...comunes} activo={activeTab === "alertas"} />
                    </TabPane>
                </Tabs>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    headersToken: state.user.headersToken,
});

export default connect(mapStateToProps)(Performance);
